/**
 * ChipContextMenu — right-click menu for a report chip.
 *
 * Right-clicking a `.report-field` opens this menu with the available
 * actions for that chip: Edit attributes / Add measurement (chips with
 * candidate-attrs), Add finding (chips in a findings organ subsection),
 * Dismiss (any editable chip), and Regenerate impression (chips in the
 * IMPRESSION section). Plain action labels, no keyboard-shortcut glyphs.
 * Jump-to-slice is not here — left-clicking a linked chip already jumps.
 *
 * Mirrors the PACS-native right-click idiom: chips are nouns, actions
 * are verbs invoked by a discrete gesture, layout never reflows.
 */

import { useState } from "react";
import { createPortal } from "react-dom";

import type { FindingReason, FindingVerdict } from "../../hooks/useReviewerFindingVerdicts";
import { REASONS } from "../reviewer/reviewerVerdictOptions";
import { useContextMenu } from "./useContextMenu";

export type ChipAction = "edit-attrs" | "measure" | "add-finding" | "dismiss" | "regenerate";

export interface ChipContextMenuProps {
  anchor: { x: number; y: number };
  /** Capabilities of the chip — items are hidden if false. */
  canEditAttrs: boolean;
  canMeasure: boolean;
  canDismiss: boolean;
  /** Chip lives in a findings organ subsection — offer "Add finding". */
  canAddFinding: boolean;
  /** Chip lives in the IMPRESSION section — offer "Regenerate impression". */
  canRegenerate: boolean;
  /** Reviewer mode: offer Agree / Disagree / Skip for a judgeable finding chip. */
  canVerdict?: boolean;
  currentVerdict?: FindingVerdict;
  onVerdict?: (
    verdict: FindingVerdict,
    reason?: FindingReason | null,
    reasonDetail?: string | null,
  ) => void;
  onAction: (action: ChipAction) => void;
  onClose: () => void;
}

const MENU_WIDTH = 220;

const ITEM_LABEL: Record<ChipAction, string> = {
  "edit-attrs": "Edit attributes",
  measure: "Add measurement",
  "add-finding": "Add finding",
  dismiss: "Dismiss",
  regenerate: "Regenerate impression",
};

export function ChipContextMenu({
  anchor,
  canEditAttrs,
  canMeasure,
  canDismiss,
  canAddFinding,
  canRegenerate,
  canVerdict,
  currentVerdict,
  onVerdict,
  onAction,
  onClose,
}: ChipContextMenuProps) {
  const { ref, style } = useContextMenu(anchor, onClose, MENU_WIDTH);
  const [showReasons, setShowReasons] = useState(false);

  const primaryItems: ChipAction[] = [];
  if (canEditAttrs) primaryItems.push("edit-attrs");
  if (canMeasure) primaryItems.push("measure");
  if (canAddFinding) primaryItems.push("add-finding");

  return createPortal(
    <div
      ref={ref}
      className="chip-context-menu"
      role="menu"
      style={style}
      onMouseDown={(e) => e.stopPropagation()}
    >
      {canVerdict && onVerdict && (
        <>
          <span className="chip-context-menu__section-label">Reviewer verdict</span>
          {!showReasons ? (
            <>
              <button
                role="menuitem"
                type="button"
                className="chip-context-menu-item"
                aria-pressed={currentVerdict === "agree"}
                onClick={() => {
                  onVerdict("agree");
                  onClose();
                }}
              >
                <span>✓ Agree</span>
              </button>
              <button
                role="menuitem"
                type="button"
                className="chip-context-menu-item"
                aria-pressed={currentVerdict === "disagree"}
                onClick={() => setShowReasons(true)}
              >
                <span>✕ Disagree…</span>
              </button>
              <button
                role="menuitem"
                type="button"
                className="chip-context-menu-item"
                aria-pressed={currentVerdict === "skip"}
                onClick={() => {
                  onVerdict("skip");
                  onClose();
                }}
              >
                <span>Skip</span>
              </button>
            </>
          ) : (
            <>
              <span className="chip-context-menu__section-label">Reason for disagreement</span>
              {REASONS.map((r) => (
                <button
                  key={r.value}
                  role="menuitem"
                  type="button"
                  className="chip-context-menu-item"
                  onClick={() => {
                    onVerdict("disagree", r.value, null);
                    onClose();
                  }}
                >
                  <span>{r.label}</span>
                </button>
              ))}
            </>
          )}
          {(primaryItems.length > 0 || canDismiss || canRegenerate) && (
            <div className="chip-context-menu-divider" />
          )}
        </>
      )}
      {primaryItems.map((action) => (
        <button
          key={action}
          role="menuitem"
          type="button"
          className="chip-context-menu-item"
          onClick={() => {
            onAction(action);
            onClose();
          }}
        >
          <span>{ITEM_LABEL[action]}</span>
        </button>
      ))}
      {canDismiss && primaryItems.length > 0 && <div className="chip-context-menu-divider" />}
      {canDismiss && (
        <button
          role="menuitem"
          type="button"
          className="chip-context-menu-item chip-context-menu-item--destructive"
          onClick={() => {
            onAction("dismiss");
            onClose();
          }}
        >
          <span>{ITEM_LABEL.dismiss}</span>
        </button>
      )}
      {canRegenerate && (primaryItems.length > 0 || canDismiss) && (
        <div className="chip-context-menu-divider" />
      )}
      {canRegenerate && (
        <button
          role="menuitem"
          type="button"
          className="chip-context-menu-item"
          onClick={() => {
            onAction("regenerate");
            onClose();
          }}
        >
          <span>{ITEM_LABEL.regenerate}</span>
        </button>
      )}
    </div>,
    document.body,
  );
}
